import {
  cylDiff,
  getADDData,
  moreThan5Diff,
  showGrayTd,
  sphDiff,
} from '@/components/PrescriptionTable/utils';
import UploadPrescriptionImg from '@/components/PrescriptionTable/components/UploadPrescriptionImg';
import { NO_DATA } from '@/utils/constants';
import {
  formatPrescriptionValueForDisplay,
  type PrescriptionDisplayFormatOptions,
} from '@/utils/number-format';
import { hasPrism } from '@/utils/prescription';
import { RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useLocale } from 'next-intl';

interface SavedPrescriptionFormProps {
  prescription: any;
  showGray?: boolean;
  showError?: boolean;
  isReadOnly?: boolean;
  showImage?: boolean;
}

export default function SavedPrescriptionForm({
  prescription,
  showGray = false,
  showError = false,
  isReadOnly = false,
  showImage = false,
}: SavedPrescriptionFormProps) {
  const t = useTranslations('account.myPrescriptions');
  const locale = useLocale();
  const formatValue = (
    value: string | number | null | undefined,
    options: Omit<PrescriptionDisplayFormatOptions, 'locale'> = {},
  ) => formatPrescriptionValueForDisplay(value, { locale, ...options });

  const innerTdClassName = 'bg-white';
  const grayTdClassName = 'text-[#a9a9a9]';
  const errorTdClassName = 'text-primary-purple';
  const tableClassName = 'bg-[#f7f7f7]';

  const PrescriptionImage = (
    <div className={`w-full row flex ${innerTdClassName} border border-[#dce1e5] rounded-b-6`}>
      <div className="w-full h-[43px] text-center font-br flex items-center justify-between pr-12 pl-12 text-14">
        {t('prescriptionImage')}
        <UploadPrescriptionImg
          className="ml-10"
          img={prescription.image_url}
          onChange={() => {}}
          allowDelete={false}
          width={32}
          height={32}
        >
          <span className="flex items-center gap-4 text-[#00000066]">
            {t('uploadText')} <RightArrowOutlined className="text-[#00000066]" />
          </span>
        </UploadPrescriptionImg>
      </div>
    </div>
  );
  const isFinalShowImage = (isReadOnly && showImage && prescription.image_url) || !isReadOnly;
  const renderPrescriptionImage = () => {
    return isFinalShowImage ? PrescriptionImage : null;
  };
  return (
    <>
      <div
        className={`prescription-table border rounded-t-8 border-[#dce1e5] ${hasPrism(prescription.prescription) || isFinalShowImage ? 'border-b-0' : 'rounded-b-8'} mt-10`}
      >
        <div className="w-full row flex text-14/[18px]">
          <div className="w-1/5 min-w-[130px] rounded-tl-8 bg-[#f7f7f7] border-r border-[#d4d4d4]">
            &nbsp;
          </div>
          <div className="w-1/5 bg-[#f7f7f7] h-48 text-center font-bs flex items-center justify-center border-r border-[#d4d4d4]">
            {t('table.sph')}
          </div>
          <div className="w-1/5 bg-[#f7f7f7] h-48 text-center font-bs flex items-center justify-center border-r border-[#d4d4d4]">
            {t('table.cyl')}
          </div>
          <div className="w-1/5 bg-[#f7f7f7] h-48 text-center font-bs flex items-center justify-center border-r border-[#d4d4d4]">
            {t('table.axis')}
          </div>
          <div className="w-1/5 bg-[#f7f7f7] rounded-tr-8 h-48 text-center font-bs flex items-center justify-center">
            {t('table.add')}
          </div>
        </div>

        {/* OD 右眼 */}
        <div className="w-full row flex rounded-t-8">
          <div className="w-1/5 min-w-[130px] bg-[#f7f7f7] border-r border-t border-[#d4d4d4] pl-10 flex items-center">
            <span className="leading-0 flex flex-col">
              <span className="text-14 font-bs">{t('table.od')}</span>
              <span className="text-12 font-br text-secondary-text mt-2">{t('table.odHint')}</span>
            </span>
          </div>
          <div
            className={`w-1/5  h-48 text-center font-br border-t flex items-center justify-center border-r border-[#d4d4d4] ${
              showError && sphDiff(prescription.prescription) ? errorTdClassName : ''
            }`}
          >
            {formatValue(prescription.prescription.od_sph, {
              showPlus: true,
              emptyText: NO_DATA,
            })}
          </div>
          <div
            className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r border-[#d4d4d4] ${
              showError && cylDiff(prescription.prescription) ? errorTdClassName : ''
            }`}
          >
            {formatValue(prescription.prescription.od_cyl, {
              showPlus: true,
              emptyText: NO_DATA,
            })}
          </div>
          <div
            className={`w-1/5  h-48 text-center font-br border-t flex items-center justify-center border-r border-[#d4d4d4] ${
              showGray && showGrayTd(prescription.prescription, 'od_axis') ? grayTdClassName : ''
            }`}
          >
            {prescription.prescription.od_axis || NO_DATA}
          </div>
          <div
            className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center ${
              showGray &&
              getADDData(prescription.prescription.od_add, prescription.prescription_type) ===
                NO_DATA
                ? grayTdClassName
                : ''
            }`}
          >
            {formatValue(
              getADDData(prescription.prescription.od_add, prescription.prescription_type),
              { showPlus: true },
            )}
          </div>
        </div>

        {/* OS 左眼 */}
        <div className="w-full row flex">
          <div className="w-1/5 min-w-[130px] bg-[#f7f7f7] border-r border-t border-[#d4d4d4] pl-10 flex items-center">
            <span className="leading-0 flex flex-col">
              <span className="text-14 font-bs">{t('table.os')}</span>
              <span className="text-12 font-br text-secondary-text mt-2">{t('table.osHint')}</span>
            </span>
          </div>
          <div
            className={`w-1/5  h-48 text-center font-br flex items-center justify-center border-r border-t border-[#d4d4d4] ${
              showError && sphDiff(prescription.prescription) ? errorTdClassName : ''
            }`}
          >
            {formatValue(prescription.prescription.os_sph, {
              showPlus: true,
              emptyText: NO_DATA,
            })}
          </div>
          <div
            className={`w-1/5 h-48 text-center font-br  flex items-center justify-center border-r border-t border-[#d4d4d4] ${
              showError && cylDiff(prescription.prescription) ? errorTdClassName : ''
            }`}
          >
            {formatValue(prescription.prescription.os_cyl, {
              showPlus: true,
              emptyText: NO_DATA,
            })}
          </div>
          <div
            className={`w-1/5  h-48 text-center font-br flex items-center justify-center border-r  border-t border-[#d4d4d4] ${
              showGray && showGrayTd(prescription.prescription, 'os_axis') ? grayTdClassName : ''
            }`}
          >
            {prescription.prescription.os_axis || NO_DATA}
          </div>
          <div
            className={`w-1/5 h-48 text-center font-br  flex items-center border-t border-[#d4d4d4] justify-center ${
              showGray &&
              getADDData(prescription.prescription.os_add, prescription.prescription_type) ===
                NO_DATA
                ? grayTdClassName
                : ''
            }`}
          >
            {formatValue(
              getADDData(prescription.prescription.os_add, prescription.prescription_type),
              { showPlus: true },
            )}
          </div>
        </div>

        {/* PD */}
        <div className="w-full row flex">
          <div className="w-1/5 min-w-[130px] bg-[#f7f7f7] border-r border-t border-[#d4d4d4] pl-10 flex items-center">
            <span className="leading-0 flex flex-col">
              <span className="text-14 font-bs">{t('table.pd')}</span>
              <span className="text-12 font-br text-secondary-text mt-2">{t('table.pdHint')}</span>
            </span>
          </div>
          {prescription.prescription.pd && (
            <div className="w-4/5  h-48 text-center font-br flex items-center justify-start pl-[33px] border-t border-[#d4d4d4]">
              {formatValue(prescription.prescription.pd)}
            </div>
          )}
          <div
            className={`w-4/5  h-48  font-br flex items-center justify-around border-t border-[#d4d4d4] ${
              showError && moreThan5Diff(prescription.prescription) ? errorTdClassName : ''
            }`}
          >
            {prescription.prescription.pd_l && prescription.prescription.pd_r ? (
              <>
                <span>
                  {formatValue(prescription.prescription.pd_r)}
                  <span
                    className={` ${
                      showError && moreThan5Diff(prescription.prescription)
                        ? errorTdClassName
                        : 'text-secondary-text'
                    }`}
                  >
                    {` ${t('table.odHint')}`}
                  </span>
                </span>
                <span>
                  {formatValue(prescription.prescription.pd_l)}
                  <span
                    className={` ${
                      showError && moreThan5Diff(prescription.prescription)
                        ? errorTdClassName
                        : 'text-secondary-text'
                    }`}
                  >
                    {` ${t('table.osHint')}`}
                  </span>
                </span>
              </>
            ) : (
              <span className="text-secondary-text" />
            )}
          </div>
        </div>
      </div>

      {hasPrism(prescription.prescription) && (
        <div
          className={`prescription-table border border-[#dce1e5] ${isFinalShowImage ? 'border-b-0' : 'rounded-b-8'} ${tableClassName}`}
        >
          <div className="w-full row flex">
            <div className={`w-1/5 min-w-[130px] border-r`}>&nbsp;</div>
            <div
              className={`w-1/5 h-48 text-center text-14 font-bb flex items-center justify-center border-r`}
              dangerouslySetInnerHTML={{
                __html: t('table.prismHorizontal'),
              }}
            />
            <div
              className={`w-1/5 h-48 text-center text-14 font-bb flex items-center justify-center border-r`}
              dangerouslySetInnerHTML={{
                __html: t('table.baseDirection'),
              }}
            />
            <div
              className={`w-1/5 h-48 text-center text-14 font-bb flex items-center justify-center border-r`}
              dangerouslySetInnerHTML={{
                __html: t('table.prismVertical'),
              }}
            />
            <div
              className={`w-1/5 h-48 text-center text-14 font-bb flex items-center justify-center`}
              dangerouslySetInnerHTML={{
                __html: t('table.baseDirection'),
              }}
            />
          </div>

          {/* OD 右眼 */}
          <div className="w-full row flex">
            <div className={`w-1/5 min-w-[130px] border-r border-t pl-12`}>
              <span className="font-bs text-14 leading-0">
                <span>{t('table.od')}</span> <br />
                <span className="text-12 font-br text-secondary-text">{t('table.odHint')}</span>
              </span>
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'od_pv') ? grayTdClassName : ''
              }`}
            >
              {formatValue(prescription.prescription.od_pv || '0.00')}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'od_bd') ? grayTdClassName : ''
              }`}
            >
              {prescription.prescription.od_bd
                ? t(`table.${prescription.prescription.od_bd}` as any)
                : '—'}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'od_pv_r') ? grayTdClassName : ''
              }`}
            >
              {formatValue(prescription.prescription.od_pv_r || '0.00')}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'od_bd_r') ? grayTdClassName : ''
              }`}
            >
              {prescription.prescription.od_bd_r
                ? t(`table.${prescription.prescription.od_bd_r}` as any)
                : '—'}
            </div>
          </div>

          {/* OS 左眼 */}
          <div className="w-full row flex">
            <div className={`w-1/5 min-w-[130px] border-r border-t pl-12`}>
              <span className="font-bs text-14 leading-0">
                <span>{t('table.os')}</span> <br />
                <span className="text-12 font-br text-secondary-text">{t('table.osHint')}</span>
              </span>
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'os_pv') ? grayTdClassName : ''
              }`}
            >
              {formatValue(prescription.prescription.os_pv || '0.00')}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'os_bd') ? grayTdClassName : ''
              }`}
            >
              {prescription.prescription.os_bd
                ? t(`table.${prescription.prescription.os_bd}` as any)
                : '—'}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'os_pv_r') ? grayTdClassName : ''
              }`}
            >
              {formatValue(prescription.prescription.os_pv_r || t('table.none'))}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center border-t ${innerTdClassName} justify-center rounded-br-[6px] ${
                showGray && showGrayTd(prescription.prescription, 'os_bd_r') ? grayTdClassName : ''
              }`}
            >
              {prescription.prescription.os_bd_r
                ? t(`table.${prescription.prescription.os_bd_r}` as any)
                : '—'}
            </div>
          </div>
        </div>
      )}

      {renderPrescriptionImage()}
    </>
  );
}
